/**
 * The reads behind the wall picker's cards: the open-report and routes-to-review
 * counts of one wall (moderator), and the viewer's own flagged routes
 * (non-moderator). The active-route and "new this week" counts live in
 * `useSprayActiveRouteCount.js`.
 * docs/spec/21j-spraywall-tab-navigation.md, "The wall card — SprayWallCard",
 * "Data".
 *
 * These are plain fetch functions. The picker's numbers are held at app level
 * by `useSprayPickerSources` (docs/spec/01f-tab-loading.md), which decides when
 * to call them; the per-opening hook that used to live here is gone.
 * `SprayReportsButton` and `SprayNeedsReviewButton` reuse the two count reads.
 */
import { collection, query, where, getDocs, getCountFromServer } from "firebase/firestore";
import { db } from "../firebase";
import { summarizeOwnAttention } from "../utils/sprayWallCardModel";

/**
 * The open reports of one wall, over every version: `sprayReports where
 * wallId == W, status == "open"` (the existing (wallId, status) index), one
 * count read. 0 on any error, so a failed read never claims a number.
 */
export function fetchOpenReportCount({ gymId, wallId }) {
  if (!gymId || !wallId) return Promise.resolve(0);
  return Promise.resolve()
    .then(() => getCountFromServer(query(
      collection(db, "gyms", gymId, "sprayReports"),
      where("wallId", "==", wallId),
      where("status", "==", "open"),
    )))
    .then((snap) => {
      const n = snap?.data?.()?.count;
      return Number.isFinite(n) ? n : 0;
    }, (e) => {
      console.error("Spray open reports count failed:", e);
      return 0;
    });
}

/**
 * The live version's routes to review of one wall: `sprayProblems where
 * wallId == W, versionId == V, status == "needsReview"` (the existing
 * (wallId, versionId, status) index), one count read; moderator only, as the
 * read rule admits that shape only through the moderator arm. 0 without a
 * live version and on any error.
 */
export function fetchNeedsReviewCount({ gymId, wallId, versionId }) {
  if (!gymId || !wallId || !versionId) return Promise.resolve(0);
  return Promise.resolve()
    .then(() => getCountFromServer(query(
      collection(db, "gyms", gymId, "sprayProblems"),
      where("wallId", "==", wallId),
      where("versionId", "==", versionId),
      where("status", "==", "needsReview"),
    )))
    .then((snap) => {
      const n = snap?.data?.()?.count;
      return Number.isFinite(n) ? n : 0;
    }, (e) => {
      console.error("Spray needs-review count failed:", e);
      return 0;
    });
}

/**
 * The viewer's own flagged routes across the gym, for the cards of a
 * non-moderator: two queries the read rule's author arm proves. A failed half
 * counts as empty, so a failed read never claims a number.
 */
export function fetchOwnAttention({ gymId, uid, walls }) {
  if (!gymId || !uid) return Promise.resolve({ reportCounts: {}, reviewCounts: {} });
  const col = collection(db, "gyms", gymId, "sprayProblems");
  const docsOf = (q, label) => Promise.resolve()
    .then(() => getDocs(q))
    .then((snap) => snap.docs.map((d) => d.data()), (e) => {
      console.error(`Spray own ${label} read failed:`, e);
      return [];
    });
  return Promise.all([
    docsOf(query(col, where("setterUid", "==", uid), where("status", "==", "needsReview")), "needs-review"),
    docsOf(query(col, where("setterUid", "==", uid), where("openReportCount", ">", 0)), "reported"),
  ]).then(([review, reported]) => summarizeOwnAttention(review, reported, walls));
}
